package br.com.vvrefeicoes.portal.data.repository

import br.com.vvrefeicoes.portal.core.util.PeriodSelection
import br.com.vvrefeicoes.portal.domain.model.MonthPerformance
import br.com.vvrefeicoes.portal.domain.model.MonthPeriod
import br.com.vvrefeicoes.portal.domain.model.RatingCounts
import br.com.vvrefeicoes.portal.domain.model.SatisfactionMonth
import br.com.vvrefeicoes.portal.domain.model.SatisfactionSnapshot
import br.com.vvrefeicoes.portal.domain.model.UnitSatisfaction
import br.com.vvrefeicoes.portal.domain.repository.AuthRepository
import br.com.vvrefeicoes.portal.domain.repository.SatisfactionDashboardRepository
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.statement.bodyAsText
import io.ktor.http.HttpHeaders
import io.ktor.http.isSuccess
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

class RemoteSatisfactionDashboardRepository(
    private val httpClient: HttpClient,
    private val baseUrl: String,
    private val authRepository: AuthRepository,
) : SatisfactionDashboardRepository {
    override suspend fun load(
        selection: PeriodSelection,
        unitId: String?,
        manager: String?,
        director: String?,
    ): SatisfactionSnapshot {
        val path = "/operational/satisfaction?${panelQuery(selection, unitId, manager, director)}"
        val response = httpClient.get(baseUrl + path) { authorize() }
        if (!response.status.isSuccess()) {
            val message =
                runCatching {
                    Json { ignoreUnknownKeys = true }
                        .decodeFromString<PanelApiErrorDto>(response.bodyAsText())
                        .message
                }.getOrNull()
            error(message ?: "HTTP ${response.status.value}")
        }
        return response.body<SatisfactionDto>().toDomain()
    }

    private fun io.ktor.client.request.HttpRequestBuilder.authorize() {
        authRepository.session.value?.token?.let { token ->
            header(HttpHeaders.Authorization, "Bearer $token")
        }
    }
}

@Serializable
private data class SatisfactionDto(
    val periodLabel: String = "",
    val months: List<PanelMonthDto> = emptyList(),
    val kpis: SatisfactionKpisDto = SatisfactionKpisDto(),
    val evolution: List<SatisfactionEvolutionDto> = emptyList(),
    val performance: List<SatisfactionPerformanceDto> = emptyList(),
    val units: List<UnitSatisfactionDto> = emptyList(),
)

@Serializable
private data class SatisfactionKpisDto(
    val totalVotes: Long = 0,
    val goalPercent: Double = 0.0,
    val participationPercent: Double = 0.0,
    val satisfactionPercent: Double = 0.0,
    val achieved: Boolean = false,
    val counts: RatingCountsDto = RatingCountsDto(),
)

@Serializable
private data class SatisfactionEvolutionDto(
    val period: PanelMonthDto = PanelMonthDto(),
    val votes: Long = 0,
    val participationPercent: Double = 0.0,
    val satisfactionPercent: Double = 0.0,
)

@Serializable
private data class SatisfactionPerformanceDto(
    val period: PanelMonthDto = PanelMonthDto(),
    val counts: RatingCountsDto = RatingCountsDto(),
)

@Serializable
private data class RatingCountsDto(
    val excellent: Int = 0,
    val good: Int = 0,
    val regular: Int = 0,
    val bad: Int = 0,
)

@Serializable
private data class UnitSatisfactionDto(
    val location: String = "",
    val votes: Long = 0,
    val counts: RatingCountsDto = RatingCountsDto(),
    val totalClients: Int = 0,
    val participationPercent: Double = 0.0,
    val satisfactionPercent: Double = 0.0,
    val achieved: Boolean = false,
)

private fun SatisfactionDto.toDomain(): SatisfactionSnapshot =
    SatisfactionSnapshot(
        periodLabel = periodLabel,
        months = months.map { MonthPeriod(it.year, it.month) },
        totalVotes = kpis.totalVotes.toInt(),
        goalPercent = kpis.goalPercent,
        participationPercent = kpis.participationPercent,
        satisfactionPercent = kpis.satisfactionPercent,
        achieved = kpis.achieved,
        counts = kpis.counts.toDomain(),
        evolution =
            evolution.map {
                SatisfactionMonth(
                    period = MonthPeriod(it.period.year, it.period.month),
                    votes = it.votes.toInt(),
                    participationPercent = it.participationPercent,
                    satisfactionPercent = it.satisfactionPercent,
                )
            },
        performance = performance.map { MonthPerformance(MonthPeriod(it.period.year, it.period.month), it.counts.toDomain()) },
        units =
            units.map {
                UnitSatisfaction(
                    location = it.location,
                    votes = it.votes.toInt(),
                    counts = it.counts.toDomain(),
                    participationPercent = it.participationPercent,
                    satisfactionPercent = it.satisfactionPercent,
                    achieved = it.achieved,
                )
            },
    )

private fun RatingCountsDto.toDomain(): RatingCounts = RatingCounts(excellent = excellent, good = good, regular = regular, bad = bad)
